import type { RfpCoverage } from "@platform/btx-plan-designer";

// FE-only "Benefits Included?" toggles (never sent to carriers) that are
// derived from the spouse/child benefits on load. Because they are derived
// rather than stored, any grid rebuild recomputes them and discards the
// analyst's choice — so if a GET returns no spouse/child data the toggle snaps
// back to "no". We capture the choice per plan id + toggle key on edit and
// overlay it back onto the plan benefits before they reach PlanEditor, so it
// survives rebuilds. LOCs use different key names — Critical Illness / Basic
// Life have one `dependentBenefitsIncluded`; Voluntary Life has separate
// `spouseBenefitsIncluded` / `childBenefitsIncluded`.
const DEPENDENT_TOGGLE_KEYS = new Set([
  "dependentBenefitsIncluded",
  "spouseBenefitsIncluded",
  "childBenefitsIncluded",
]);

// Overrides are keyed by `${planId}::${benefitKey}` so a plan can carry more
// than one toggle (Voluntary Life's spouse and child). Neither a plan id (UUID)
// nor a benefit key contains "::", so the split is unambiguous.
const overrideKey = (planId: string, benefitKey: string): string =>
  `${planId}::${benefitKey}`;

/**
 * Collect the toggle value per plan id + key from an edited coverage, for the
 * toggle cells that were just changed.
 */
export const captureDependentToggleOverrides = (
  coverage: RfpCoverage,
): Record<string, string> => {
  const updates: Record<string, string> = {};

  for (const { planId, benefitKey } of coverage._dirtyCells ?? []) {
    if (!DEPENDENT_TOGGLE_KEYS.has(benefitKey)) continue;

    const plan = coverage.benefitPlans?.find((p) => p.id === planId);
    const value = (plan?.benefits as Record<string, unknown> | undefined)?.[
      benefitKey
    ];

    if (typeof value === "string")
      updates[overrideKey(planId, benefitKey)] = value;
  }

  return updates;
};

// Critical Illness hides the "Dependent Benefits Included?" row from Quotes
// (see QUOTES_HIDDEN_BENEFIT_KEYS in collapseAmountRows), so the analyst can
// no longer dirty it directly to unlock the spouse/child rows. But
// transformCriticalIllnessBenefitsToApi only forwards spouse/child edits when
// dependentBenefitsIncluded is exactly "yes" — without it, an edit to a
// dependent field would save the toggle-gated type fields but drop them from
// the API payload. Derive the toggle from whichever spouse/child type field
// was just edited so the save still carries it. Basic Life's to-API transform
// reads the spouse/child type fields directly and never gates on this toggle,
// so it needs no equivalent derivation.
//
// Spouse has three independently-editable groups sharing this one toggle —
// Benefit Description, Benefit Maximum, and Guarantee Issue — so all three
// type keys must trigger derivation, not just Description. A future group
// added to Critical Illness's spouse/child definitions needs its own "Type"
// key appended here too.
const CRITICAL_ILLNESS_DEPENDENT_TYPE_KEYS = [
  "spouseBenefitDescriptionType",
  "spouseBenefitMaximumType",
  "spouseGuaranteeIssueType",
  "childBenefitDescriptionType",
] as const;

const isAnswered = (value: unknown): boolean =>
  typeof value === "string" && value !== "";

const hasDependentBenefit = (value: unknown): boolean =>
  isAnswered(value) && value !== "notIncluded";

/**
 * Derive the hidden `dependentBenefitsIncluded` override for Critical Illness
 * plans whose spouse/child type field was just edited, keyed the same way as
 * `captureDependentToggleOverrides` so the two can be merged. Skips a plan
 * whose dependent type fields are all unanswered ("" or undefined) — that's
 * an analyst clearing the fields back to blank, not an explicit "no", and
 * stamping "no" would trigger dependent clearing in the to-API transform.
 */
export const deriveHiddenDependentToggleOverrides = (
  coverage: RfpCoverage,
): Record<string, string> => {
  const updates: Record<string, string> = {};

  const editedPlanIds = new Set(
    (coverage._dirtyCells ?? [])
      .filter(({ benefitKey }) =>
        (CRITICAL_ILLNESS_DEPENDENT_TYPE_KEYS as readonly string[]).includes(
          benefitKey,
        ),
      )
      .map(({ planId }) => planId),
  );

  for (const planId of editedPlanIds) {
    const benefits = coverage.benefitPlans?.find((p) => p.id === planId)
      ?.benefits as Record<string, unknown> | undefined;

    const isAnyAnswered = CRITICAL_ILLNESS_DEPENDENT_TYPE_KEYS.some((key) =>
      isAnswered(benefits?.[key]),
    );

    if (!isAnyAnswered) continue;

    const hasDependents = CRITICAL_ILLNESS_DEPENDENT_TYPE_KEYS.some((key) =>
      hasDependentBenefit(benefits?.[key]),
    );

    updates[overrideKey(planId, "dependentBenefitsIncluded")] = hasDependents
      ? "yes"
      : "no";
  }

  return updates;
};

/**
 * Overlay the captured toggle choices onto the coverage benefits. Plans without
 * an override keep their derived value.
 */
export const applyDependentToggleOverrides = (
  coverage: RfpCoverage | undefined,
  overrides: Record<string, string>,
): RfpCoverage | undefined => {
  if (!coverage || Object.keys(overrides).length === 0) return coverage;

  return {
    ...coverage,
    benefitPlans: coverage.benefitPlans?.map((plan) => {
      if (!plan.benefits) return plan;

      const patch: Record<string, string> = {};
      for (const [composite, value] of Object.entries(overrides)) {
        const [planId, benefitKey] = composite.split("::");
        if (planId === plan.id && benefitKey) patch[benefitKey] = value;
      }

      if (Object.keys(patch).length === 0) return plan;

      return { ...plan, benefits: { ...plan.benefits, ...patch } };
    }),
  };
};
